"use client";

import { ComponentProps, MouseEvent, useState } from "react";
import { Tooltip } from "react-tooltip";

import { SliderDisplayNumber } from "@/components/form/slider/SliderDisplayNumber";
import { SliderSkeleton } from "@/components/form/slider/SliderSkeleton";
import { Label } from "@/components/text/Label";
import { SoftText } from "@/components/text/SoftText";
import { PitchRecommendation } from "@/types/defs/audioQuality/pitchRecommendation";
import { getPitchWindowSliderHighlight } from "@/utils/audio/quality/pitchWindow/getPitchWindowSliderHighlight";

type SliderSkeletonProps = ComponentProps<typeof SliderSkeleton>;

// Pitch-shift slider with sweet-spot guidance: composes the generic SliderSkeleton with a sweet-spot gradient on the track and a hover tooltip that activates only when the cursor is over the highlighted region. Owns the pitch-window derivation, the hover state, and the Tooltip mount.
export function PitchSlider({
  sliderProps,
  className,
  pitchRecommendation,
}: {
  sliderProps: Omit<SliderSkeletonProps, "trackStyle">;
  className: string;
  pitchRecommendation: PitchRecommendation;
}) {
  const [shouldShowTooltip, setShouldShowTooltip] = useState(false);

  const highlight = getPitchWindowSliderHighlight(pitchRecommendation);
  const hasHighlight = highlight !== null;

  const handleMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    if (!hasHighlight) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const hoverPositionPercent =
      ((event.clientX - rect.left) / rect.width) * 100;
    setShouldShowTooltip(
      hoverPositionPercent >= highlight.hoverTooltip.activeStartPercent &&
        hoverPositionPercent <= highlight.hoverTooltip.activeEndPercent
    );
  };

  if (sliderProps.value === undefined || sliderProps.value.length === 0) {
    return null;
  }

  const isRangeSlider = sliderProps.value.length === 2;

  return (
    <div className={className} data-tutorial="pitch-shift">
      <Label elementType="label">Pitch shift (semitones)</Label>
      <SoftText
        elementType="p"
        className="mb-2 mt-1 text-sm font-normal text-gray-500"
      >
        Shifts the entire vocal up or down in pitch.
      </SoftText>

      <div className="flex items-center">
        {isRangeSlider && (
          <SliderDisplayNumber
            value={sliderProps.value[0]}
            decimalToPercent={false}
            className="mr-4"
          />
        )}
        <div
          className="relative flex-grow"
          onMouseMove={hasHighlight ? handleMouseMove : undefined}
          onMouseLeave={
            hasHighlight ? () => setShouldShowTooltip(false) : undefined
          }
          data-tooltip-id={hasHighlight ? "pitch-slider-tooltip" : undefined}
        >
          {hasHighlight && (
            <Tooltip
              id="pitch-slider-tooltip"
              content={highlight.hoverTooltip.content}
              isOpen={shouldShowTooltip}
              place="top"
              noArrow
              className="z-10"
            />
          )}
          <SliderSkeleton
            {...sliderProps}
            trackStyle={hasHighlight ? highlight.trackStyle : null}
          />
        </div>
        <SliderDisplayNumber
          value={isRangeSlider ? sliderProps.value[1] : sliderProps.value[0]}
          decimalToPercent={false}
          className="ml-4"
        />
      </div>
    </div>
  );
}
